import { Lang } from "../i18n";
import { Footer } from "./Footer";
import { Nav } from "./Nav";
import { Seo, type SeoPage } from "./Seo";

type LegalDocument = "cookie" | "terms";

type LegalTable = {
  headers: string[];
  rows: string[][];
};

type LegalSection = {
  title: string;
  body: string[];
  table?: LegalTable;
};

const operatorName = {
  en: "MytePro Technology Limited",
  zh: "MytePro Technology Limited",
};

const updatedAt: Record<Lang, string> = {
  en: "Last updated: June 16, 2026",
  zh: "生效日期：2026 年 6 月 16 日",
};

const documentIntro: Record<LegalDocument, Record<Lang, { label: string; title: string; subtitle: string }>> = {
  cookie: {
    en: {
      label: "Cookie Policy",
      title: "Cookie Policy",
      subtitle:
        "This policy explains how ClawEase uses cookies, pixels, local storage, and similar technologies for site operations, analytics, advertising measurement, and abuse prevention.",
    },
    zh: {
      label: "Cookie 政策",
      title: "Cookie 政策",
      subtitle: "本政策说明 ClawEase 如何使用 Cookie、像素、本地存储及类似技术，用于网站运行、统计分析、广告效果衡量和安全防护。",
    },
  },
  terms: {
    en: {
      label: "Terms of Use",
      title: "Terms of Use",
      subtitle:
        "These terms describe the rules for visiting the ClawEase website, requesting demos, and using public demo experiences provided by the ClawEase product brand.",
    },
    zh: {
      label: "使用条款",
      title: "使用条款",
      subtitle: "本条款说明您访问 ClawEase 官网、预约演示和使用公开演示体验时适用的基本规则。",
    },
  },
};

const cookieTechnologyTables: Record<Lang, LegalTable> = {
  en: {
    headers: ["Technology / category", "Provider", "Examples", "Purpose", "Typical duration"],
    rows: [
      [
        "Necessary website operation",
        "ClawEase / hosting providers",
        "Routing state, language preference, form submission state, security and diagnostic logs",
        "Operate the website, keep pages and forms usable, prevent abuse, and diagnose errors.",
        "Session-based where possible. Security and diagnostic logs are not advertising cookies and are generally retained only as needed for security, troubleshooting, and legal compliance.",
      ],
      [
        "Google Ads conversion measurement",
        "Google Ads",
        "Google Ads tags, gtag.js, click IDs such as gclid, gbraid, wbraid, and conversion cookies such as _gcl_aw, _gcl_dc, _gcl_gb, _gcl_au",
        "Attribute ad clicks and lead submissions, measure campaign effectiveness, and avoid duplicate conversion reporting.",
        "Google Ads conversion cookies are typically stored for up to 90 days. URL click IDs may appear in campaign metadata and are retained only as needed for attribution and business records.",
      ],
      [
        "X Pixel conversion measurement",
        "X Corp.",
        "X Pixel / Universal Website Tag (twq), event ID tw-rd1hz-rd1ir, twclid, pixel or browser identifiers, and X cookies or local storage where X sets them",
        "Attribute X ad clicks and lead submissions, measure campaign effectiveness, and report aggregated ad performance.",
        "Controlled by X and browser settings. X advertising cookies or local storage may persist for up to 13 months, about 395 days, depending on region, device, and X policy; event records are retained according to X policy.",
      ],
      [
        "TikTok Pixel advertising measurement",
        "TikTok",
        "TikTok Pixel (ttq), pixel ID D8PR433C77U5LM9ITN9G, TikTok click IDs such as ttclid, _ttp, and pixel or browser identifiers where TikTok sets them",
        "Attribute TikTok ad visits, measure campaign performance, and support aggregated advertising reports.",
        "Controlled by TikTok and browser settings. TikTok advertising cookies or local storage may persist according to TikTok policy, your region, device, and browser settings.",
      ],
      [
        "Meta Pixel advertising measurement",
        "Meta",
        "Meta Pixel (fbq), pixel ID 2793874577653185, Meta click IDs such as fbclid, _fbp, _fbc, and pixel or browser identifiers where Meta sets them",
        "Attribute Meta ad visits and lead submissions, measure campaign performance, and support aggregated advertising reports.",
        "Controlled by Meta and browser settings. Meta advertising cookies or local storage may persist according to Meta policy, your region, device, and browser settings.",
      ],
      [
        "Campaign parameters",
        "ClawEase, Google Ads, X, TikTok, Meta, and analytics or service providers",
        "utm_source, utm_medium, utm_campaign, utm_term, utm_content, ad_source, entry_source, source, ttclid, fbclid",
        "Understand which campaign or channel brought a demo request and split Google/X/TikTok/Meta reporting where configured.",
        "Stored in the submitted lead record only when a demo request is sent, and retained according to the Privacy Policy and business or legal record needs.",
      ],
    ],
  },
  zh: {
    headers: ["技术 / 类别", "提供方", "示例标识", "使用目的", "典型有效期 / 保留期"],
    rows: [
      [
        "网站必要运行",
        "ClawEase / 托管服务商",
        "路由状态、语言偏好、表单提交状态、安全与诊断日志",
        "保障网站和表单可用、防止滥用、排查错误。",
        "尽量采用会话级。安全与诊断日志不属于广告 Cookie，一般仅在安全、排障和合规所需期间保留。",
      ],
      [
        "Google Ads 转化衡量",
        "Google Ads",
        "Google Ads 标签、gtag.js、gclid、gbraid、wbraid 点击 ID，以及 _gcl_aw、_gcl_dc、_gcl_gb、_gcl_au 等转化 Cookie",
        "归因广告点击和线索提交、衡量广告效果、避免重复转化统计。",
        "Google Ads 转化 Cookie 通常最长保存 90 天。URL 中的点击 ID 可能作为广告活动元数据出现，仅在归因和业务记录所需期间保留。",
      ],
      [
        "X Pixel 转化衡量",
        "X Corp.",
        "X Pixel / Universal Website Tag (twq)、事件 ID tw-rd1hz-rd1ir、twclid、像素或浏览器标识，以及 X 可能设置的 Cookie 或本地存储",
        "归因 X 广告点击和线索提交、衡量广告效果、输出汇总广告表现。",
        "由 X 及浏览器设置控制。X 广告 Cookie 或本地存储可能最长保留约 13 个月（约 395 天），具体取决于地区、设备和 X 政策；事件记录按 X 政策保留。",
      ],
      [
        "TikTok Pixel 广告衡量",
        "TikTok",
        "TikTok Pixel (ttq)、Pixel ID D8PR433C77U5LM9ITN9G、ttclid 等 TikTok 点击 ID、_ttp，以及 TikTok 可能设置的像素或浏览器标识",
        "归因 TikTok 广告访问、衡量广告活动效果，并支持汇总广告表现报告。",
        "由 TikTok 及浏览器设置控制。TikTok 广告 Cookie 或本地存储的保留期取决于 TikTok 政策、地区、设备和浏览器设置。",
      ],
      [
        "Meta Pixel 广告衡量",
        "Meta",
        "Meta Pixel (fbq)、Pixel ID 2793874577653185、fbclid 等 Meta 点击 ID、_fbp、_fbc，以及 Meta 可能设置的像素或浏览器标识",
        "归因 Meta 广告访问和线索提交、衡量广告活动效果，并支持汇总广告表现报告。",
        "由 Meta 及浏览器设置控制。Meta 广告 Cookie 或本地存储的保留期取决于 Meta 政策、地区、设备和浏览器设置。",
      ],
      [
        "广告活动参数",
        "ClawEase、Google Ads、X、TikTok、Meta 及分析或服务提供商",
        "utm_source、utm_medium、utm_campaign、utm_term、utm_content、ad_source、entry_source、source、ttclid、fbclid",
        "判断预约演示来自哪个广告活动或渠道，并在配置情况下用于 Google/X/TikTok/Meta 上报或归因。",
        "仅在用户提交预约演示时进入线索记录，并按隐私政策及业务或法律记录需求保留。",
      ],
    ],
  },
};

const cookieSections: Record<Lang, LegalSection[]> = {
  en: [
    {
      title: "1. Who operates this website",
      body: [
        `ClawEase is a product brand operated by ${operatorName.en}.`,
        "This Cookie Policy applies to the ClawEase website, demo request pages, marketing landing pages, and related public demo experiences.",
      ],
    },
    {
      title: "2. What cookies and similar technologies are",
      body: [
        "Cookies are small text files placed on your browser or device. Similar technologies may include local storage, pixels, tags, SDKs, click identifiers, and device or browser signals.",
        "These technologies help the website function, remember basic preferences, understand site usage, measure advertising performance, prevent abuse, and improve product and marketing experiences.",
      ],
    },
    {
      title: "3. How we use these technologies",
      body: [
        "Necessary technologies support core website functions, security, routing, form submission, language selection, fraud prevention, and service reliability.",
        "Analytics technologies help us understand aggregated visits, traffic sources, page performance, and user interactions so we can improve the website and product experience.",
        "Advertising and measurement technologies, including Google Ads tags, X Pixel, TikTok Pixel, and Meta Pixel where configured, help attribute visits and lead submissions to campaigns and measure campaign effectiveness.",
      ],
    },
    {
      title: "4. Current cookies and tracking technologies",
      body: [
        "The table below describes the main cookies and tracking technologies currently used on the ClawEase website. Exact cookie names and lifetimes may change when advertising platforms update their tags, browser rules change, or you clear cookies.",
        "Where a third-party provider controls the cookie or local storage, the duration is governed by that provider's policy, your region, and your browser or device settings.",
      ],
      table: cookieTechnologyTables.en,
    },
    {
      title: "5. Advertising measurement and personal information",
      body: [
        "For advertising conversion measurement, we may report event and campaign metadata such as click IDs, campaign parameters, page visits, and interaction events.",
        "We do not send the content of demo request or contact forms, such as your name, email address, phone number, company name, or message, to Google Ads, X Pixel, TikTok Pixel, or Meta Pixel as conversion event parameters.",
      ],
    },
    {
      title: "6. Third-party technologies",
      body: [
        "Some cookies, pixels, or tags may be provided by third-party services that help us host the website, measure advertising, analyze traffic, secure forms, or deliver communications.",
        "Those third parties may process identifiers or technical information according to their own policies. We do not control third-party websites or platforms linked from this website.",
      ],
    },
    {
      title: "7. Managing cookies",
      body: [
        "When you first visit the website, you can allow or reject non-essential advertising and measurement technologies through the cookie banner. If you reject them, Google Ads, X Pixel, TikTok Pixel, and Meta Pixel tracking will not be enabled by the website.",
        "You can also manage cookies and similar technologies through your browser or device settings. Most browsers allow you to block, delete, or receive alerts for cookies.",
        "Blocking cookies may affect attribution, analytics, language preferences, form behavior, security checks, or parts of the website experience.",
      ],
    },
    {
      title: "8. Updates and contact",
      body: [
        "We may update this Cookie Policy from time to time. The latest version will be posted on this page.",
        "If you have questions about this policy, please contact us at info@clawease.ai.",
      ],
    },
  ],
  zh: [
    {
      title: "一、网站运营主体",
      body: [
        `ClawEase 是由 ${operatorName.zh} 运营的产品品牌。本 Cookie 政策适用于 ClawEase 官网、预约演示页面、营销落地页及相关公开演示体验。`,
      ],
    },
    {
      title: "二、什么是 Cookie 和类似技术",
      body: [
        "Cookie 是存储在浏览器或设备中的小型文本文件。类似技术可能包括本地存储、像素、标签、SDK、点击标识以及设备或浏览器信号。",
        "这些技术可以帮助网站正常运行、记住基础偏好、了解访问情况、衡量广告效果、防止滥用，并改进产品和营销体验。",
      ],
    },
    {
      title: "三、我们如何使用这些技术",
      body: [
        "必要技术用于支持网站基础功能、安全防护、路由跳转、表单提交、语言选择、反滥用及服务稳定性。",
        "统计分析技术用于了解汇总访问、流量来源、页面表现和交互情况，以便改进网站和产品体验。",
        "广告与衡量技术，包括在配置情况下使用的 Google Ads 标签、X Pixel、TikTok Pixel 和 Meta Pixel，用于将访问或线索提交归因至对应广告活动，并衡量广告效果。",
      ],
    },
    {
      title: "四、当前使用的 Cookie 和跟踪技术",
      body: [
        "下表列出 ClawEase 官网当前主要使用的 Cookie 和跟踪技术。具体 Cookie 名称和有效期可能因广告平台更新标签、浏览器规则变化或您清除 Cookie 而变化。",
        "如 Cookie 或本地存储由第三方提供方控制，其有效期还会受到该第三方政策、您所在地区以及浏览器或设备设置影响。",
      ],
      table: cookieTechnologyTables.zh,
    },
    {
      title: "五、广告统计与个人信息",
      body: [
        "在广告转化统计中，我们可能上报事件和广告活动元数据，例如点击 ID、广告活动参数、页面访问和交互事件。",
        "我们不会将预约演示页面或联系表单中的姓名、邮箱、手机号、公司名称或留言内容作为转化事件参数发送给 Google Ads、X Pixel、TikTok Pixel 或 Meta Pixel。",
      ],
    },
    {
      title: "六、第三方技术",
      body: [
        "部分 Cookie、像素或标签可能由第三方服务提供，用于帮助我们托管网站、衡量广告、分析流量、保护表单安全或发送沟通信息。",
        "该等第三方可能按照其自身政策处理标识或技术信息。我们无法控制从本网站跳转至的第三方网站或平台。",
      ],
    },
    {
      title: "七、管理 Cookie",
      body: [
        "您首次访问网站时，可以通过 Cookie 弹窗允许或拒绝非必要的广告与衡量技术。如您选择拒绝，本网站不会启用 Google Ads、X Pixel、TikTok Pixel 和 Meta Pixel 跟踪。",
        "您也可以通过浏览器或设备设置管理 Cookie 和类似技术。大多数浏览器允许您阻止、删除 Cookie，或在 Cookie 被设置时进行提醒。",
        "关闭 Cookie 可能影响广告归因、统计分析、语言偏好、表单行为、安全校验或部分网站体验。",
      ],
    },
    {
      title: "八、政策更新与联系方式",
      body: [
        "我们可能不时更新本 Cookie 政策。最新版本将在本页面发布。",
        "如您对本政策有任何问题，请通过 info@clawease.ai 联系我们。",
      ],
    },
  ],
};

const termsSections: Record<Lang, LegalSection[]> = {
  en: [
    {
      title: "1. Acceptance of these terms",
      body: [
        `ClawEase is a product brand operated by ${operatorName.en}.`,
        "These Terms of Use apply to the ClawEase website, demo request pages, marketing landing pages, and public demo experiences that link to or post these terms.",
        "By accessing or using the website, submitting a demo request, or using a public demo experience, you agree to these Terms of Use. If you do not agree, please do not use the website or public demos.",
        "The website and demos are intended for business users. If you are a minor or have not reached the legal age of majority in your country or region, you should review these terms with a parent or legal guardian and use the website only with appropriate permission.",
        "We may update these Terms of Use from time to time. If you continue to use the website after revised terms are posted, you are deemed to have accepted the updated terms.",
      ],
    },
    {
      title: "2. What ClawEase provides",
      body: [
        "ClawEase provides AI-powered customer request automation, front desk workflow automation, and demo experiences for business users evaluating software services.",
        "Public pages and demos are provided for product evaluation and informational purposes. They do not create real appointments, medical records, insurance records, payments, or legally binding customer transactions unless a separate production agreement, order form, or service contract is signed.",
        "Any paid subscription, production deployment, service level, data processing arrangement, integration, or professional implementation service will be governed by the applicable written agreement between the parties.",
      ],
    },
    {
      title: "3. Acceptable use of the website",
      body: [
        "You are responsible for your actions on the website. You may use the website only for lawful purposes and in accordance with these terms.",
        "You may not use the website or website content for any illegal purpose, to solicit illegal activity, to infringe the rights of ClawEase or any third party, or to violate privacy, intellectual property, publicity, or other rights.",
        "You may not impersonate another person or entity, misrepresent your identity or authority, submit false or misleading information, or use the website in a fraudulent or deceptive manner.",
        "You may not post, submit, or transmit spam, malware, harmful code, obscene content, hateful content, discriminatory content, infringing content, or content that is unlawful, abusive, threatening, defamatory, or otherwise objectionable.",
        "You may not scan, probe, test, bypass, or breach the security or authentication measures of the website; interfere with the operation of the website; impose an unreasonable load on our systems; scrape the website at scale; or attempt to access data, systems, or accounts without authorization.",
        "Unless we give prior written permission, you may not use the website or public demos for your own commercial advertising, resale, service bureau use, benchmarking publication, or competitive misuse.",
      ],
    },
    {
      title: "4. Public demos and sensitive information",
      body: [
        "Public demo pages use fictional or sample scenarios. Please do not submit real medical records, insurance details, payment information, identity documents, or other sensitive personal information in public demos.",
        "Demo outputs, screenshots, sample conversations, and sample workflows are illustrative only. They may not reflect every production configuration, customer system, legal requirement, or operational constraint.",
        "If you submit information through the demo request page or contact forms, that information is handled according to our Privacy Policy and Cookie Policy.",
      ],
    },
    {
      title: "5. No professional advice or regulated services",
      body: [
        "ClawEase is a software automation product. It does not provide medical, dental, veterinary, legal, financial, insurance, emergency, or other regulated professional advice or services.",
        "Businesses using ClawEase are responsible for their own licenses, professional decisions, compliance obligations, customer communications, approvals, supervision, and final service delivery.",
        "Any AI-generated or automated output should be reviewed and configured by the business using the service before it is relied upon in a production workflow.",
      ],
    },
    {
      title: "6. Privacy, cookies, and contact forms",
      body: [
        "Our collection and use of information is described in the Privacy Policy and Cookie Policy. By using the website, you acknowledge that information may be processed as described in those policies.",
        "Demo request and contact forms are intended for business users who want to evaluate, purchase, or integrate ClawEase services. We use submitted information to respond to inquiries, arrange product demos, discuss purchase or onboarding needs, and keep necessary business, security, and compliance records.",
      ],
    },
    {
      title: "7. Feedback and submissions",
      body: [
        "If you submit comments, ideas, feature requests, suggestions, or other feedback about the website or ClawEase, you grant us a non-exclusive, worldwide, royalty-free right to use, reproduce, modify, disclose, distribute, and otherwise exploit that feedback without restriction or compensation.",
        "This feedback license does not change how we handle personal information submitted through demo request pages or contact forms, which remains subject to the Privacy Policy.",
        "Please do not submit confidential, sensitive, or third-party proprietary information unless we have entered into a written agreement that covers that information.",
      ],
    },
    {
      title: "8. Third-party platforms, links, and trademarks",
      body: [
        "The website may mention third-party platforms, channels, or brands such as Google, Chrome, Meta, Facebook, Instagram, TikTok, WhatsApp, LINE, X, SMS, email, or other systems to describe supported channels, integrations, advertising measurement, or examples.",
        "Unless expressly stated in a written agreement, ClawEase is not affiliated with, endorsed by, sponsored by, or officially partnered with those third-party brands. Third-party names and marks belong to their respective owners.",
        "The website may include links to third-party websites or platforms for your convenience. We are not responsible for third-party websites, products, services, content, policies, or practices. A link does not imply endorsement, sponsorship, or approval.",
      ],
    },
    {
      title: "9. Intellectual property, trademarks, and copyright",
      body: [
        "The website, product names, logos, copy, design, screenshots, illustrations, demos, and other materials are owned by or licensed to the operator, except for third-party materials identified as such.",
        "You may not copy, modify, reproduce, distribute, publicly display, transmit, mirror, scrape, frame, reverse engineer, decompile, disassemble, create derivative works from, or commercially exploit the website, demos, software, or product materials without prior written permission, except as permitted by law.",
        "All ClawEase names, logos, product marks, and related brand elements are trademarks or brand assets of the operator or its licensors. You may not use them in a way that suggests affiliation, endorsement, sponsorship, or authorization without prior written permission.",
      ],
    },
    {
      title: "10. Disclaimers",
      body: [
        "The website and public demos are provided on an as-is and as-available basis. We do not guarantee uninterrupted availability, error-free operation, specific commercial outcomes, or suitability for every business scenario.",
        "To the maximum extent permitted by applicable law, we disclaim all express, implied, and statutory warranties relating to the website, public demos, and website content, including warranties of merchantability, fitness for a particular purpose, title, quiet enjoyment, non-infringement, accuracy, completeness, reliability, timeliness, and security.",
        "We do not warrant that the website, public demos, or content will be uninterrupted, secure, error-free, free of harmful components, or suitable for your particular business, system, jurisdiction, or compliance needs.",
      ],
    },
    {
      title: "11. Limitation of liability",
      body: [
        "To the maximum extent permitted by applicable law, we will not be liable for indirect, incidental, consequential, special, exemplary, punitive, or loss-of-profit damages, including loss of data, goodwill, revenue, business opportunity, service interruption, computer damage, system failure, or cost of substitute services arising from or related to the website, public demos, website content, or these terms.",
        "To the maximum extent permitted by applicable law, our total liability arising out of or related to the website, public demos, website content, or these terms will not exceed one hundred US dollars (US$100) or the minimum amount that cannot be excluded under applicable law, whichever is greater.",
        "Some jurisdictions do not allow certain exclusions or limitations of liability. In those jurisdictions, our liability will be limited to the maximum extent permitted by law.",
      ],
    },
    {
      title: "12. Indemnity",
      body: [
        "You agree to indemnify, defend, and hold harmless the operator, its affiliates, officers, directors, employees, agents, licensors, contractors, and service providers from and against claims, demands, disputes, allegations, investigations, liabilities, damages, losses, costs, and expenses, including reasonable attorneys' fees, arising out of or related to your access to or use of the website, your submissions, your violation of these terms, your violation of law, or your infringement of third-party rights.",
        "We reserve the right to assume control of the defense of any matter subject to indemnification, and you agree to cooperate with us in asserting available defenses.",
      ],
    },
    {
      title: "13. Enforcement rights",
      body: [
        "We may investigate suspected violations of these terms or conduct that affects the website, public demos, our systems, or other users.",
        "We may, at our discretion and without notice, remove or disable access to content, restrict access to the website or demos, block abusive traffic, preserve records, or take any action we deem appropriate to protect the website, users, third parties, or our rights.",
      ],
    },
    {
      title: "14. Governing law and dispute resolution",
      body: [
        "Unless a separate written agreement states otherwise, these terms and any dispute arising from or related to the website, public demos, or these terms are governed by the laws of the People's Republic of China, without regard to conflict-of-law principles.",
        "Unless mandatory law requires otherwise, any dispute arising from or related to these terms will be submitted to the competent court with jurisdiction over the operator's domicile.",
      ],
    },
    {
      title: "15. Severability and contact",
      body: [
        "If any provision of these terms is held invalid, void, or unenforceable, that provision will be deemed severable and will not affect the validity and enforceability of the remaining provisions.",
        "If you have questions about these terms, please contact us at info@clawease.ai.",
      ],
    },
  ],
  zh: [
    {
      title: "一、接受本条款",
      body: [
        `ClawEase 是由 ${operatorName.zh} 运营的产品品牌。`,
        "本使用条款适用于发布或链接至本条款的 ClawEase 官网、预约演示页面、营销落地页及公开演示体验。",
        "您访问或使用本网站、提交预约演示请求或使用公开演示体验，即表示您同意本使用条款。如您不同意，请不要使用本网站或公开演示。",
        "本网站和演示主要面向商业用户。如您属于未成年人或未达到所在国家/地区的法定成年年龄，应在父母或法定监护人陪同下阅读本条款，并在获得适当许可后使用本网站。",
        "我们可能不时更新本使用条款。更新后的条款发布后，如您继续使用本网站，即视为您接受更新后的条款。",
      ],
    },
    {
      title: "二、ClawEase 提供的内容",
      body: [
        "ClawEase 提供面向商业用户的软件产品信息、AI 客户请求自动化、AI 前台工作流自动化及产品演示体验。",
        "公开页面和演示仅用于产品评估和信息展示。除非另行签署正式生产服务协议、订单或服务合同，否则公开演示不会创建真实预约、医疗记录、保险记录、支付交易或具有法律约束力的客户交易。",
        "任何付费订阅、生产部署、服务等级、数据处理安排、系统集成或专业实施服务，均以双方另行签署或确认的书面协议为准。",
      ],
    },
    {
      title: "三、网站可接受使用",
      body: [
        "您应对自己在本网站上的行为负责，并仅能为合法目的、按照本条款使用本网站。",
        "您不得将本网站或网站内容用于任何违法目的，不得诱导违法活动，不得侵犯 ClawEase 或任何第三方的权利，也不得侵犯他人的隐私权、知识产权、肖像/名誉等合法权益。",
        "您不得冒充他人或实体、虚假陈述身份或授权、提交虚假或误导性信息，或以欺诈、欺骗方式使用本网站。",
        "您不得发布、提交或传输垃圾信息、恶意软件、有害代码、淫秽低俗内容、仇恨或歧视性内容、侵权内容，或违法、辱骂、威胁、诽谤或其他不适当内容。",
        "您不得扫描、探测、测试、绕过或破坏本网站安全或身份验证措施；不得干扰网站运行；不得对系统造成不合理负载；不得大规模抓取网站；不得未经授权访问数据、系统或账号。",
        "未经我们事先书面许可，您不得将本网站或公开演示用于自身商业广告、转售、服务外包、公开基准测试或竞争性滥用。",
      ],
    },
    {
      title: "四、公开演示与敏感信息",
      body: [
        "公开演示页面使用虚拟或示例场景。请勿在公开演示中提交真实医疗记录、保险信息、支付信息、身份证件或其他敏感个人信息。",
        "演示输出、截图、示例对话和示例流程仅用于说明产品能力，不代表所有生产配置、客户系统、法律要求或运营限制。",
        "如您通过预约演示页面或联系表单提交信息，该等信息将按照我们的隐私政策和 Cookie 政策处理。",
      ],
    },
    {
      title: "五、不提供专业建议或受监管服务",
      body: [
        "ClawEase 是软件自动化产品，不提供医疗、牙科、兽医、法律、金融、保险、紧急救助或其他受监管的专业建议或服务。",
        "使用 ClawEase 的企业应自行负责其许可资质、专业判断、合规义务、客户沟通、审批监督和最终服务交付。",
        "任何 AI 生成或自动化输出在进入生产工作流前，均应由使用该服务的企业自行审查、配置和确认。",
      ],
    },
    {
      title: "六、隐私、Cookie 与联系表单",
      body: [
        "我们对信息的收集和使用请参见隐私政策和 Cookie 政策。您使用本网站，即表示您知悉相关信息可能按照该等政策进行处理。",
        "预约演示和联系表单面向希望评估、购买或集成 ClawEase 服务的商业用户。我们使用您提交的信息来回复咨询、安排产品演示、讨论购买或接入需求，并保留必要的业务、安全和合规记录。",
      ],
    },
    {
      title: "七、反馈与提交内容",
      body: [
        "如果您向我们提交关于本网站或 ClawEase 的评论、想法、功能需求、建议或其他反馈，您授予我们非排他、全球范围、免许可费的权利，可不受限制且无需向您支付报酬地使用、复制、修改、披露、分发和以其他方式利用该等反馈。",
        "上述反馈授权不改变我们对预约演示页面或联系表单中个人信息的处理方式，该等信息仍适用隐私政策。",
        "除非我们已签署覆盖相关信息的书面协议，请勿提交保密、敏感或第三方专有信息。",
      ],
    },
    {
      title: "八、第三方平台、链接与商标",
      body: [
        "本网站可能提及 Google、Chrome、Meta、Facebook、Instagram、TikTok、WhatsApp、LINE、X、短信、邮箱或其他第三方平台、渠道、品牌或系统，用于描述支持的渠道、集成能力、广告衡量或示例场景。",
        "除非另有书面协议明确说明，ClawEase 与该等第三方品牌不存在关联、背书、赞助或官方合作关系。第三方名称和标识归其各自权利人所有。",
        "本网站可能为方便您访问而包含指向第三方网站或平台的链接。我们不对第三方网站、产品、服务、内容、政策或实践负责。链接不代表我们对其背书、赞助或认可。",
      ],
    },
    {
      title: "九、知识产权、商标与版权",
      body: [
        "本网站中的产品名称、标识、文案、设计、截图、插图、演示和其他材料由运营主体拥有或依法获得授权，明确标识为第三方材料的除外。",
        "未经事先书面许可，您不得复制、修改、转载、分发、公开展示、传输、镜像、抓取、框架嵌套、反向工程、反编译、反汇编、创建衍生作品或商业利用本网站、演示、软件或产品材料，但法律另有允许的除外。",
        "ClawEase 名称、标识、产品标记及相关品牌元素属于运营主体或其许可方的商标或品牌资产。未经事先书面许可，您不得以暗示关联、背书、赞助或授权的方式使用。",
      ],
    },
    {
      title: "十、免责声明",
      body: [
        "本网站和公开演示按现状及可用状态提供。我们不保证网站持续可用、完全无误、一定产生特定商业效果，或适合所有业务场景。",
        "在适用法律允许的最大范围内，我们不就本网站、公开演示或网站内容作出任何明示、默示或法定保证，包括但不限于适销性、特定用途适用性、权利归属、安宁使用、不侵权、准确性、完整性、可靠性、及时性和安全性的保证。",
        "我们不保证本网站、公开演示或内容将持续可用、安全、无误、无有害组件，或适合您的特定业务、系统、司法辖区或合规需求。",
      ],
    },
    {
      title: "十一、责任限制",
      body: [
        "在适用法律允许的最大范围内，我们不对因本网站、公开演示、网站内容或本条款引起或与之相关的任何间接、附带、后果性、特殊、示范性、惩罚性或利润损失承担责任，包括数据、商誉、收入、商业机会损失、服务中断、计算机损坏、系统故障或替代服务成本。",
        "在适用法律允许的最大范围内，我们因本网站、公开演示、网站内容或本条款产生的全部责任总额不超过一百美元（US$100），或适用法律不得排除的最低责任金额，以较高者为准。",
        "部分司法辖区不允许排除或限制某些责任。在该等司法辖区，我们的责任将在法律允许的最大范围内受到限制。",
      ],
    },
    {
      title: "十二、赔偿",
      body: [
        "因您访问或使用本网站、您的提交内容、您违反本条款、违反法律或侵犯第三方权利而导致任何第三方或政府机关提出索赔、请求、争议、指控、调查、责任、损害、损失、成本和费用（包括合理律师费）的，您同意赔偿并使运营主体及其关联方、管理人员、董事、员工、代理、许可方、承包商和服务提供商免受损害。",
        "对于需要您赔偿的事项，我们保留自行控制抗辩的权利，您同意配合我们提出可用抗辩。",
      ],
    },
    {
      title: "十三、执行权利",
      body: [
        "我们可以调查涉嫌违反本条款或影响本网站、公开演示、系统或其他用户的行为。",
        "我们可以自行决定且无需事先通知，移除或禁用内容、限制访问本网站或演示、阻断滥用流量、保留记录，或采取我们认为适当的措施，以保护本网站、用户、第三方或我们的权利。",
      ],
    },
    {
      title: "十四、适用法律与争议解决",
      body: [
        "除非另有书面协议约定，本条款以及因本网站、公开演示或本条款引起或与之相关的任何争议，均适用中华人民共和国法律，不适用冲突法规则。",
        "除非强制性法律另有规定，因本条款引起或与之相关的任何争议，应提交运营主体住所地有管辖权的人民法院解决。",
      ],
    },
    {
      title: "十五、可分割性与联系方式",
      body: [
        "如果本条款中的任何条款被认定为无效、不可执行或因任何原因无法执行，该条款应被视为可分割，不影响其余条款的有效性和可执行性。",
        "如您对本条款有任何问题，请通过 info@clawease.ai 联系我们。",
      ],
    },
  ],
};

const documents: Record<LegalDocument, Record<Lang, LegalSection[]>> = {
  cookie: cookieSections,
  terms: termsSections,
};

const legalTableColumnClasses = ["w-[15%]", "w-[13%]", "w-[28%]", "w-[20%]", "w-[24%]"];

export function LegalDocumentPage({ defaultLang = "en" as Lang, document }: { defaultLang?: Lang; document: LegalDocument }) {
  const lang = defaultLang;
  const intro = documentIntro[document][lang];
  const sections = documents[document][lang];

  return (
    <div className="min-h-screen bg-[#f8f9fa] text-[#100f12] font-sans">
      <Seo lang={lang} page={document as SeoPage} />
      <Nav lang={lang} />
      <main className="relative overflow-hidden px-5 pt-32 sm:px-10 lg:pt-36">
        <div
          className="pointer-events-none absolute inset-x-0 top-0 h-[460px]"
          style={{
            background:
              "radial-gradient(circle at 18% 2%, rgba(153,108,255,.18), transparent 30%), radial-gradient(circle at 82% 4%, rgba(233,48,201,.12), transparent 32%), linear-gradient(180deg,#fff 0%,rgba(248,249,250,.96) 72%,#f8f9fa 100%)",
          }}
        />

        <section className="relative mx-auto max-w-[980px] pb-20 lg:pb-28">
          <div className="text-center">
            <div className="inline-flex rounded-full bg-[#f3eefa] px-3 py-1 text-[12px] font-extrabold uppercase tracking-[0.12em] text-[#5223b1]">
              {intro.label}
            </div>
            <h1 className="mx-auto mt-4 max-w-[820px] text-[40px] font-extrabold leading-[1.05] tracking-[-0.04em] text-[#100f12] sm:text-[54px]">
              {intro.title}
            </h1>
            <p className="mx-auto mt-4 max-w-[720px] text-[15px] font-semibold leading-7 text-[#775f78]">{intro.subtitle}</p>
            <p className="mt-4 text-[13px] font-bold text-[#8a768c]">{updatedAt[lang]}</p>
          </div>

          <article className="mx-auto mt-9 rounded-[24px] border border-[#eaddea] bg-white p-6 shadow-[0_24px_76px_rgba(46,22,152,.08)] sm:p-8 lg:p-10">
            <div className="space-y-9">
              {sections.map((section) => (
                <section key={section.title}>
                  <h2 className="text-[22px] font-extrabold tracking-[-0.02em] text-[#17141c]">{section.title}</h2>
                  <div className="mt-4 space-y-3 text-[15px] font-semibold leading-8 text-[#5f5565]">
                    {section.body.map((paragraph) => (
                      <p key={paragraph}>{paragraph}</p>
                    ))}
                  </div>
                  {section.table ? (
                    <div className="mt-6 overflow-x-auto rounded-[18px] border border-[#eaddea] bg-white shadow-[0_16px_40px_rgba(82,35,177,.06)]">
                      <table className="w-full min-w-[860px] border-collapse text-left text-[13px]">
                        <thead className="bg-[#f7f2fb] text-[#5223b1]">
                          <tr>
                            {section.table.headers.map((header, index) => (
                              <th
                                key={header}
                                className={`border-b border-[#eaddea] px-4 py-3 font-extrabold ${legalTableColumnClasses[index] ?? ""}`}
                              >
                                {header}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="text-[#5f5565]">
                          {section.table.rows.map((row) => (
                            <tr key={row.join("|")} className="align-top [&:last-child>td]:border-b-0">
                              {row.map((cell, index) => (
                                <td
                                  key={`${row[0]}-${index}`}
                                  className={`border-b border-[#f0e8f3] px-4 py-4 font-semibold leading-6 ${
                                    legalTableColumnClasses[index] ?? ""
                                  }`}
                                >
                                  {cell}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : null}
                </section>
              ))}
            </div>
          </article>
        </section>
      </main>
      <div className="relative overflow-hidden bg-[#f8f9fa]">
        <div
          className="pointer-events-none absolute inset-0 opacity-90"
          style={{
            background:
              "radial-gradient(circle at 24% 6%, rgba(153,108,255,.13), transparent 30%), radial-gradient(circle at 76% 14%, rgba(233,48,201,.10), transparent 28%), linear-gradient(180deg,rgba(248,249,250,.98) 0%,rgba(242,239,249,.92) 100%)",
          }}
        />
        <div className="relative">
          <Footer lang={lang} />
        </div>
      </div>
    </div>
  );
}
